import { styled } from "@mui/material/styles";
import React from "react";
import { CheckIcon } from "./icons";
import { focusRing, reducedMotion } from "./tokens";
const ToggleRoot = styled("button")({
    ...focusRing,
    position: "relative",
    width: "1.625rem",
    height: "var(--j-icon)",
    flexShrink: 0,
    padding: 0,
    border: 0,
    borderRadius: "calc(var(--j-icon) / 2)",
    background: "var(--j-line-strong)",
    cursor: "pointer",
    transition: "background-color var(--j-dur-fade)",
    "&::after": {
        content: '""',
        position: "absolute",
        top: "var(--j-sp-0-5)",
        left: "var(--j-sp-0-5)",
        width: "0.75rem",
        height: "0.75rem",
        borderRadius: "50%",
        background: "var(--j-surface)",
        transition: "transform var(--j-dur-fade) var(--j-ease)"
    },
    "&[aria-checked=true]": {
        background: "var(--j-accent)"
    },
    "&[aria-checked=true]::after": {
        transform: "translateX(0.625rem)"
    },
    "&:disabled": {
        opacity: 0.4,
        cursor: "default"
    },
    [reducedMotion]: {
        "&::after": {
            transition: "none"
        }
    }
});
export function Toggle({ checked, onChange, type = "button", ...rest }) {
    return /*#__PURE__*/ React.createElement(ToggleRoot, {
        type: type,
        role: "switch",
        "aria-checked": checked,
        onClick: ()=>onChange?.(!checked),
        ...rest
    });
}
const boxBase = {
    width: "0.875rem",
    height: "0.875rem",
    flexShrink: 0,
    padding: 0,
    display: "grid",
    placeItems: "center",
    border: "1px solid var(--j-line-cb)",
    background: "var(--j-surface)",
    color: "var(--j-on-accent)",
    transition: "background-color 150ms, border-color 150ms, border-width 150ms"
};
const CheckboxRoot = styled("button")({
    ...focusRing,
    ...boxBase,
    borderRadius: "var(--j-r-xs)",
    cursor: "pointer",
    "&[aria-checked=true], &[aria-checked=mixed], &[data-checked=true], &[data-checked=mixed]": {
        background: "var(--j-accent)",
        borderColor: "var(--j-accent)"
    },
    "&:disabled, &[data-disabled=true]": {
        opacity: 0.4,
        cursor: "default"
    }
});
const Dash = styled("i")({
    width: "0.5rem",
    height: "0.125rem",
    borderRadius: "var(--j-r-xs)",
    background: "var(--j-on-accent)"
});
export function Checkbox({ checked, indeterminate = false, onChange, type = "button", disabled, ...rest }) {
    const state = indeterminate && !checked ? "mixed" : checked;
    const mark = state === "mixed" ? /*#__PURE__*/ React.createElement(Dash, null) : checked && /*#__PURE__*/ React.createElement(CheckIcon, {
        size: 12,
        strokeWidth: 3
    });
    if (!onChange) {
        return /*#__PURE__*/ React.createElement(CheckboxRoot, {
            as: "span",
            "data-checked": String(state),
            "data-disabled": disabled,
            "aria-hidden": true
        }, mark);
    }
    return /*#__PURE__*/ React.createElement(CheckboxRoot, {
        type: type,
        role: "checkbox",
        "aria-checked": state,
        disabled: disabled,
        onClick: ()=>onChange(!checked),
        ...rest
    }, mark);
}
const RadioRoot = styled("button")({
    ...focusRing,
    ...boxBase,
    borderRadius: "50%",
    cursor: "pointer",
    "&[aria-checked=true], &[data-checked=true]": {
        borderWidth: "0.25rem",
        borderColor: "var(--j-accent)"
    },
    "&:disabled, &[data-disabled=true]": {
        opacity: 0.4,
        cursor: "default"
    }
});
export function Radio({ checked, onChange, type = "button", disabled, ...rest }) {
    if (!onChange) {
        return /*#__PURE__*/ React.createElement(RadioRoot, {
            as: "span",
            "data-checked": String(checked),
            "data-disabled": disabled,
            "aria-hidden": true
        });
    }
    return /*#__PURE__*/ React.createElement(RadioRoot, {
        type: type,
        role: "radio",
        "aria-checked": checked,
        disabled: disabled,
        onClick: ()=>onChange(true),
        ...rest
    });
}
